import type { JobList, JobSummary, OpeningsOutcome } from '@/features/jobs/types';

import { kioskFetch } from './kioskFetch';

/**
 * The openings list [KJM-1, KJM-2, KJM-3].
 *
 * ⚠️ Hand-written types, same as `identityLookup.ts` and for the same reason: `npm run api:types` reads an
 * `openapi.json` the backend serves at `/docs` but never writes to disk. Until it does, a backend field
 * rename surfaces here as a missing card field rather than a typecheck failure. Tracked as follow-up.
 */

const OPENINGS_PATH = '/jobs';

/**
 * Bounded, and short. A candidate is standing at the device, and this request fans out to Bullhorn behind
 * the backend — whose own upstream timeout is 8s, so anything much past that is waiting for an answer the
 * backend has already stopped waiting for.
 */
const REQUEST_TIMEOUT_MS = 12_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

const asString = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const asNumber = (value: unknown): number | null => (typeof value === 'number' ? value : null);

/**
 * Reads one order, or rejects it.
 *
 * `jobOrderId` is the only field that cannot be missing: it is the react key and the identity the detail
 * screen fetches by, so an order without one is not renderable. Everything else degrades to `null`, which
 * every part of the card is already built to handle — a missing shift range is the normal case for roughly
 * one order in four [decision 0083], so treating absence as a parse failure would drop real jobs.
 */
function readOrder(value: unknown): JobSummary | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const order = value as Record<string, unknown>;

  const jobOrderId = order.jobOrderId;
  if (typeof jobOrderId !== 'number') return undefined;

  return {
    jobOrderId,
    title: asString(order.title),
    clientName: asString(order.clientName),
    city: asString(order.city),
    zip: asString(order.zip),
    shiftType: asString(order.shiftType),
    shiftStart: asString(order.shiftStart),
    shiftEnd: asString(order.shiftEnd),
    shiftDays: asString(order.shiftDays),
    jobType: asString(order.jobType),
    payRate: asString(order.payRate),
    numOpenings: asNumber(order.numOpenings),
    publishedDescription: asString(order.publishedDescription),
    featured: order.featured === true,
    inRadius: order.inRadius === true,
  };
}

/**
 * Parses the response rather than trusting it (§ 6, "validate at the boundary").
 *
 * An unparseable order is **dropped, not fatal**. That is a deliberate asymmetry with the identity lookup,
 * which treats an unrecognised body as unreachable: there, guessing would put a real person in the wrong
 * flow; here, one malformed row among five should not cost the candidate the other four.
 *
 * **`orders` being an array is now the whole envelope check.** `radiusMiles` used to be required here too,
 * and decision 0158 retired the radius — leaving the guard would have meant that the moment the backend
 * stops sending the field, every response fails to parse and every candidate is told to see a
 * representative. One consequence is deliberate and worth knowing: `{ orders: [] }` on its own now parses
 * as a real empty list rather than as an unreachable backend, which is what this file already says an
 * empty pool IS [decision 0095] — the candidate is enqueued, and the screen says a recruiter is on the way.
 */
function readJobList(body: unknown): JobList | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const payload = body as Record<string, unknown>;

  if (!Array.isArray(payload.orders)) return undefined;

  return {
    venueId: asString(payload.venueId) ?? '',
    source: asString(payload.source) ?? '',
    radiusModel: asString(payload.radiusModel) ?? '',
    orders: highlightAtMostOne(
      payload.orders.map(readOrder).filter((order): order is JobSummary => order !== undefined),
    ),
    handoffReason: asString(payload.handoffReason),
  };
}

/**
 * "The big push" is singular [KJM-3a AC-6; decision 0362; `UX_ksco_one_featured`], so a list that arrives
 * with two or more stars is a recruiter's mistake, not a ranking: **highlight none** rather than pick one,
 * because an arbitrary pick would present the mistake as ISS's push.
 *
 * The backend already resolves this, and this does not replace it — it is the second entry point the
 * invariant names. It runs here, where the list and the opened detail both come from, so no screen has to
 * remember to count. It reads nothing but the stars: not `inRadius`, not the ZIP, not the position, and it
 * never moves an order [0362; 0363].
 */
function highlightAtMostOne(orders: readonly JobSummary[]): readonly JobSummary[] {
  return orders.filter((order) => order.featured).length > 1
    ? orders.map((order) => (order.featured ? { ...order, featured: false } : order))
    : orders;
}

export interface OpeningsRequest {
  /*
   * ⛔ **No `venueId`, and its absence is the point** [03-us-kad-6c; decisions 0204, 0221]. The field was
   * `readonly venueId: string` and required, fed from `lib/venue.ts`'s hard-coded pilot constant — so
   * deleting it at the call site alone would not have compiled, and forcing it past would have sent
   * `venueId=undefined`, which is the same `400` as sending a real one. The venue is the credential's now:
   * the api reads it off the kiosk session cookie `kioskFetch` carries, and `forbidNonWhitelisted` answers
   * `400 property venueId should not exist` to a request that names one.
   */
  /**
   * Who is asking, when they were matched.
   *
   * **No `zip`, and no `miles`.** The candidate's ZIP is on their Bullhorn record and the backend reads it
   * there — the kiosk never had one to send. The threshold is the configured default and KJM-1's definition
   * of done says no request parameter moves it; the backend answers 400 to one, and this shape is the
   * client-side half of that promise.
   */
  readonly candidateId?: number | undefined;
}

/**
 * Fetches the ordered openings.
 *
 * Never throws. Every failure is a value: an exception escaping into a render tree is the dead end BR-K7
 * forbids.
 */
export async function fetchOpenings(request: OpeningsRequest): Promise<OpeningsOutcome> {
  const query = new URLSearchParams();
  if (request.candidateId !== undefined) query.set('candidateId', String(request.candidateId));
  // A bare `?` is not a parameter, but it is a difference in a request somebody will one day compare.
  const suffix = query.size === 0 ? '' : `?${query.toString()}`;

  let response: Response;
  try {
    response = await kioskFetch(`${apiBaseUrl()}${OPENINGS_PATH}${suffix}`, {
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { kind: 'unreachable' };
  }

  if (!response.ok) return { kind: 'unreachable' };

  const body: unknown = await response.json().catch(() => undefined);
  const list = readJobList(body);
  return list === undefined ? { kind: 'unreachable' } : { kind: 'list', list };
}
